/**
 * What the feedback prompt remembers about this browser. The automatic prompt shows once per browser session (so
 * once per sign-in in practice) until the person opts out. Storage can be missing or throw (private windows, blocked
 * site data), and then the prompt simply behaves as if nothing was remembered.
 */
export type FeedbackPromptMemory = {
  shownThisSession: () => boolean;
  markShown: () => void;
  optedOut: () => boolean;
  optOut: () => void;
};

const SHOWN_KEY = "ots.feedbackPrompt.shown";
const OPT_OUT_KEY = "ots.feedbackPrompt.optOut";

const read = (storage: () => Storage, key: string): boolean => {
  try {
    return storage().getItem(key) === "1";
  } catch {
    return false;
  }
};

const write = (storage: () => Storage, key: string): void => {
  try {
    storage().setItem(key, "1");
  } catch {
    // Nothing to remember with; the prompt may show again, which is harmless.
  }
};

export const browserFeedbackPromptMemory: FeedbackPromptMemory = {
  shownThisSession: () => read(() => window.sessionStorage, SHOWN_KEY),
  markShown: () => write(() => window.sessionStorage, SHOWN_KEY),
  optedOut: () => read(() => window.localStorage, OPT_OUT_KEY),
  optOut: () => write(() => window.localStorage, OPT_OUT_KEY),
};

/** In-memory stand-in for stories and tests. */
export const createFeedbackPromptMemory = (initial: {shown?: boolean; optedOut?: boolean} = {}): FeedbackPromptMemory => {
  let shown = initial.shown ?? false;
  let optedOut = initial.optedOut ?? false;
  return {
    shownThisSession: () => shown,
    markShown: () => { shown = true; },
    optedOut: () => optedOut,
    optOut: () => { optedOut = true; },
  };
};
